'use strict';

/**
 * EstimateTemplate — shared HTML-renderer для смет.
 *
 * Sprint 19e (2026-05-09).
 *
 * Используется:
 *   - PdfService._buildHtml() — server-side для PDF.
 *   - В будущем: public/js/app/reset-print.js — browser print().
 *
 * Pure CommonJS module без зависимостей (работает в Node + Vite-bundle).
 *
 * Контракт: buildHtml(estimateData, options) → HTML string.
 * options может содержать переопределения форматтеров (currency и т.п.).
 */

// ─── Helpers (pure) ────────────────────────────────────────────────────────

function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function defaultFmtCurrency(amount) {
    const n = Math.round(Number(amount) || 0);
    return '$' + n.toLocaleString('en-US');
}

function defaultFmtDate(value) {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const [y, m, d] = value.substring(0, 10).split('-');
        return `${d}.${m}.${y}`;
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('ru-RU');
}

function calculateServiceTotal(service) {
    const price = Number(service.price) || 0;
    const qty = Number(service.quantity) || 0;
    const markup = Number(service.markup) || 0;
    const base = price * qty;
    return base * (1 + markup / 100);
}

// ─── Main HTML builder ─────────────────────────────────────────────────────

/**
 * @param {Object} estimate — распарсенный estimate object с полями:
 *   services, hotels, flights, otherServices, optionalServices,
 *   hiddenMarkup, partnerCommission, taxRate,
 *   clientName, clientEmail, clientPhone, paxCount, tourStart, tourEnd,
 *   programDescription, bookingTerms.
 * @param {Object} [options]
 *   - meta: { clientName?: string } — fallback для client name
 *   - fmtCurrency: (n) => string
 *   - fmtDate: (v) => string
 *   - esc: (s) => string  (HTML escape)
 * @returns {string} HTML string для рендера в PDF/print
 */
function buildHtml(estimate, options = {}) {
    const e = estimate || {};
    const meta = options.meta || {};
    const esc = options.esc || escapeHtml;
    const fmt = options.fmtCurrency || defaultFmtCurrency;
    const fmtDate = options.fmtDate || defaultFmtDate;

    const services = Array.isArray(e.services) ? e.services : [];
    const hotels = Array.isArray(e.hotels) ? e.hotels : [];
    const flights = Array.isArray(e.flights) ? e.flights : [];
    const otherServices = Array.isArray(e.otherServices) ? e.otherServices : [];
    const optionalServices = Array.isArray(e.optionalServices) ? e.optionalServices : [];

    const all = [...services, ...hotels, ...flights, ...otherServices];

    const baseCostForHiddenMarkup = all.reduce((sum, s) => {
        if (s.excludeFromMarkup) return sum;
        return sum + (Number(s.price) || 0) * (Number(s.quantity) || 0);
    }, 0);

    const hiddenMarkup = Number(e.hiddenMarkup) || 0;
    const partnerCommission = Number(e.partnerCommission) || 0;

    const processService = (svc) => {
        const serviceBaseCost = (Number(svc.price) || 0) * (Number(svc.quantity) || 0);
        const serviceWithMarkup = calculateServiceTotal(svc);
        let hiddenMarkupPortion = 0;
        if (!svc.excludeFromMarkup && baseCostForHiddenMarkup > 0) {
            const proportion = serviceBaseCost / baseCostForHiddenMarkup;
            hiddenMarkupPortion = (baseCostForHiddenMarkup * hiddenMarkup / 100) * proportion;
        }
        const totalWithHiddenMarkup = serviceWithMarkup + hiddenMarkupPortion;
        return {
            ...svc,
            _baseCost: serviceBaseCost,
            _displayPrice: totalWithHiddenMarkup
        };
    };

    const servicesForPrint = services.map(processService);
    const hotelsForPrint = hotels.map(processService);
    const flightsForPrint = flights.map(processService);
    const otherForPrint = otherServices.map(processService);

    const allForPrint = [
        ...servicesForPrint, ...hotelsForPrint, ...flightsForPrint, ...otherForPrint
    ];

    if (partnerCommission > 0) {
        const totalAfterHidden = allForPrint.reduce((s, x) => s + x._displayPrice, 0);
        const totalCommission = totalAfterHidden * (partnerCommission / 100);
        const totalBase = allForPrint.reduce((s, x) => s + x._baseCost, 0);
        allForPrint.forEach(s => {
            if (totalBase > 0) {
                const proportion = s._baseCost / totalBase;
                s._displayPrice += totalCommission * proportion;
            }
        });
    }

    const finalTotal = allForPrint.reduce((s, x) => s + x._displayPrice, 0);

    const today = new Date();
    const dateStr = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;

    // ─── Main services table — grouped by day ─────────────────────────────
    const servicesByDay = new Map();
    servicesForPrint.forEach(s => {
        const day = s.day || 0;
        if (!servicesByDay.has(day)) servicesByDay.set(day, []);
        servicesByDay.get(day).push(s);
    });
    const sortedDayKeys = [...servicesByDay.keys()].sort((a, b) => Number(a) - Number(b));

    let mainRows = '';
    sortedDayKeys.forEach((dayKey) => {
        const items = servicesByDay.get(dayKey);
        if (sortedDayKeys.length > 1 && Number(dayKey) > 0) {
            mainRows += `
                <tr style="background: #e5e7eb;">
                    <td colspan="3" style="padding: 8pt; border: 2px solid #333; font-weight: 700; text-align: center; font-size: 10pt;">
                        День ${esc(dayKey)}
                    </td>
                </tr>
            `;
        }
        items.forEach(svc => {
            const desc = svc.description || svc.name || '';
            const time = svc.startTime ? ` (${svc.startTime}${svc.endTime ? '-' + svc.endTime : ''})` : '';
            mainRows += `
                <tr>
                    <td style="padding: 6pt; border: 1px solid #999; vertical-align: top;">
                        <strong>${esc(svc.name)}</strong>${esc(time)}
                        ${desc && desc !== svc.name ? `<div style="font-size:9pt;color:#555;margin-top:2pt;">${esc(desc)}</div>` : ''}
                    </td>
                    <td style="padding: 6pt; border: 1px solid #999; text-align: center; vertical-align: top;">${esc(svc.quantity || 1)}</td>
                    <td style="padding: 6pt; border: 1px solid #999; text-align: right; vertical-align: top;">${fmt(svc._displayPrice)}</td>
                </tr>
            `;
        });
    });

    const renderSimpleTable = (items, title) => {
        if (items.length === 0) return '';
        const rows = items.map(svc => `
            <tr>
                <td style="padding: 6pt; border: 1px solid #999;">
                    <strong>${esc(svc.name)}</strong>
                    ${svc.description && svc.description !== svc.name ? `<div style="font-size:9pt;color:#555;">${esc(svc.description)}</div>` : ''}
                </td>
                <td style="padding: 6pt; border: 1px solid #999; text-align: center;">${esc(svc.quantity || 1)}</td>
                <td style="padding: 6pt; border: 1px solid #999; text-align: right;">${fmt(svc._displayPrice)}</td>
            </tr>
        `).join('');
        return `
            <h3 style="margin-top: 20pt; margin-bottom: 8pt; font-size: 12pt;">${esc(title)}</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 10pt;">
                <thead>
                    <tr style="background: #f3f4f6;">
                        <th style="padding: 6pt; border: 1px solid #999; text-align: left;">Услуга</th>
                        <th style="padding: 6pt; border: 1px solid #999; text-align: center; width: 60pt;">Кол-во</th>
                        <th style="padding: 6pt; border: 1px solid #999; text-align: right; width: 90pt;">Стоимость</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>
        `;
    };

    const hotelsTable = renderSimpleTable(hotelsForPrint, 'Отели и размещение');
    const flightsTable = renderSimpleTable(flightsForPrint, 'Авиабилеты');
    const otherTable = renderSimpleTable(otherForPrint, 'Прочие услуги');

    // Optional services
    let optionalSection = '';
    if (optionalServices.length > 0) {
        const optionalProcessed = optionalServices.map(processService);
        const rows = optionalProcessed.map(svc => `
            <tr>
                <td style="padding: 6pt; border: 1px solid #999;">
                    <strong>${esc(svc.name)}</strong>
                    ${svc.description && svc.description !== svc.name ? `<div style="font-size:9pt;color:#555;">${esc(svc.description)}</div>` : ''}
                </td>
                <td style="padding: 6pt; border: 1px solid #999; text-align: center;">${esc(svc.quantity || 1)}</td>
                <td style="padding: 6pt; border: 1px solid #999; text-align: right;">${fmt(svc._displayPrice)}</td>
            </tr>
        `).join('');
        optionalSection = `
            <h3 style="margin-top: 20pt; margin-bottom: 8pt; font-size: 12pt; color: #6b7280;">Опциональные услуги (на выбор)</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 10pt;">
                <thead>
                    <tr style="background: #f3f4f6;">
                        <th style="padding: 6pt; border: 1px solid #999; text-align: left;">Услуга</th>
                        <th style="padding: 6pt; border: 1px solid #999; text-align: center; width: 60pt;">Кол-во</th>
                        <th style="padding: 6pt; border: 1px solid #999; text-align: right; width: 90pt;">Стоимость</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>
        `;
    }

    // Prepayment section
    const prepaymentItems = all.filter(s => s.requires_prepayment || s.prepayment);
    const prepaymentManual = Number(e.prepaymentManual) || 0;
    const prepaymentAuto = prepaymentItems.reduce((sum, s) => {
        const base = (Number(s.price) || 0) * (Number(s.quantity) || 0);
        const markup = 1 + (Number(s.markup) || 0) / 100;
        return sum + base * markup;
    }, 0);
    const prepaymentTotal = prepaymentAuto + prepaymentManual;
    let prepaymentSection = '';
    if (prepaymentTotal > 0) {
        const itemRows = prepaymentItems.map(svc => `
            <tr>
                <td style="padding: 4pt 6pt; border: 1px solid #f9a8d4;">${esc(svc.name)}</td>
                <td style="padding: 4pt 6pt; border: 1px solid #f9a8d4; text-align: right;">${fmt((Number(svc.price) || 0) * (Number(svc.quantity) || 0) * (1 + (Number(svc.markup) || 0) / 100))}</td>
            </tr>
        `).join('');
        prepaymentSection = `
            <div style="margin-top: 20pt; padding: 12pt; background: #fdf2f8; border: 2px solid #f9a8d4; border-radius: 4pt;">
                <h4 style="margin: 0 0 8pt 0; color: #9d174d; font-size: 11pt;">💳 Предоплата за бронирование</h4>
                ${itemRows ? `
                    <table style="width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 6pt;">
                        ${itemRows}
                    </table>
                ` : ''}
                ${prepaymentManual > 0 ? `
                    <div style="display: flex; justify-content: space-between; padding: 4pt 6pt; font-size: 9pt;">
                        <span>Дополнительная сумма за работу над бронированиями</span>
                        <span>${fmt(prepaymentManual)}</span>
                    </div>
                ` : ''}
                <div style="display: flex; justify-content: space-between; padding-top: 6pt; margin-top: 6pt; border-top: 1px solid #f9a8d4; font-weight: 700; font-size: 11pt; color: #9d174d;">
                    <span>Итого предоплата:</span>
                    <span>${fmt(prepaymentTotal)}</span>
                </div>
            </div>
        `;
    }

    // ─── Document HTML ─────────────────────────────────────────────────────
    const docTitle = `Коммерческое предложение${e.clientName || meta.clientName ? ' — ' + esc(e.clientName || meta.clientName) : ''}`;

    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${docTitle}</title>
    <style>
        @page { size: A4; margin: 15mm 10mm; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111; font-size: 10pt; line-height: 1.4; }
        h1 { font-size: 18pt; margin: 0 0 10pt 0; }
        h2 { font-size: 14pt; margin: 16pt 0 8pt 0; }
        h3 { margin: 12pt 0 6pt 0; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12pt; border-bottom: 2pt solid #333; margin-bottom: 16pt; }
        .summary { background: #f9fafb; padding: 10pt; border-left: 3pt solid #2563eb; margin-bottom: 16pt; font-size: 10pt; }
        .summary-row { display: flex; justify-content: space-between; padding: 2pt 0; }
        .grand-total { background: #2563eb; color: white; padding: 12pt; margin-top: 16pt; border-radius: 4pt; display: flex; justify-content: space-between; font-size: 14pt; font-weight: 700; }
    </style>
</head>
<body>
    <div class="header">
        <div>
            <h1>${docTitle}</h1>
            <div style="color: #666; font-size: 9pt;">от ${dateStr}</div>
        </div>
    </div>

    <div class="summary">
        ${e.clientName || meta.clientName ? `<div class="summary-row"><strong>Клиент:</strong><span>${esc(e.clientName || meta.clientName)}</span></div>` : ''}
        ${e.clientPhone ? `<div class="summary-row"><strong>Телефон:</strong><span>${esc(e.clientPhone)}</span></div>` : ''}
        ${e.clientEmail ? `<div class="summary-row"><strong>Email:</strong><span>${esc(e.clientEmail)}</span></div>` : ''}
        ${e.paxCount ? `<div class="summary-row"><strong>Гостей (PAX):</strong><span>${esc(e.paxCount)}</span></div>` : ''}
        ${e.tourStart ? `<div class="summary-row"><strong>Даты тура:</strong><span>${fmtDate(e.tourStart)}${e.tourEnd ? ' — ' + fmtDate(e.tourEnd) : ''}</span></div>` : ''}
    </div>

    ${e.programDescription ? `<div style="margin-bottom: 16pt;"><h2>Описание программы</h2><div style="white-space: pre-wrap;">${esc(e.programDescription)}</div></div>` : ''}

    ${mainRows ? `
        <h2>Программа тура</h2>
        <table style="width: 100%; border-collapse: collapse; font-size: 10pt;">
            <thead>
                <tr style="background: #f3f4f6;">
                    <th style="padding: 6pt; border: 1px solid #999; text-align: left;">Услуга</th>
                    <th style="padding: 6pt; border: 1px solid #999; text-align: center; width: 60pt;">Кол-во</th>
                    <th style="padding: 6pt; border: 1px solid #999; text-align: right; width: 90pt;">Стоимость</th>
                </tr>
            </thead>
            <tbody>${mainRows}</tbody>
        </table>
    ` : ''}

    ${hotelsTable}
    ${flightsTable}
    ${otherTable}

    <div class="grand-total">
        ИТОГО: ${fmt(finalTotal)}
    </div>

    ${prepaymentSection}
    ${optionalSection}

    ${e.bookingTerms ? `<div style="margin-top: 24pt; padding-top: 12pt; border-top: 1px solid #999;"><h3>Условия бронирования</h3><div style="font-size: 9pt; white-space: pre-wrap;">${esc(e.bookingTerms)}</div></div>` : ''}
</body>
</html>`;
}

// Sprint 23 W8 v2: tri-environment export pattern.
//
// History:
//   - Original: had `export { ... }` after `module.exports = ...` — broke Jest
//     because ESM `export` is parse-time and Jest's default CJS parser crashed.
//   - Chat 1 fix (commit 2af621e): removed all `export {}`, kept module.exports
//     only. Worked for Jest + Vite production bundles.
//   - Regression (this commit): when staging serves the raw file (e.g. Vite dev
//     server OR Sprint 23 W8 added a code path that bypasses the bundle), the
//     browser parses it as ESM, hits line `module.exports = ...` at runtime,
//     and crashes with `ReferenceError: module is not defined`. Cascade kills
//     app.js initialization.
//
// Resolution: runtime-guarded exports — no parse-time ESM syntax (Jest happy),
// runtime-detected env (browser/node) gets the right one (Vite + native ESM happy).
//
// HOW EACH ENV GETS THE EXPORTS:
//   • Jest / Node `require()`:        module.exports = {...}     ✓ (CJS path)
//   • Vite-bundled production import: esbuild interop sees module.exports → wraps as default+namespace ✓
//   • Raw ESM in browser (regression case): `module` is undefined → guarded
//     branch skipped → no crash. Functions still exposed via `window.EstimateTemplate`. ✓
const __exports = {
    buildHtml,
    escapeHtml,
    defaultFmtCurrency,
    defaultFmtDate,
    calculateServiceTotal
};

if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
    module.exports = __exports;
}

if (typeof window !== 'undefined') {
    // Legacy callers (reset-print.js) read window.EstimateTemplate.buildHtml.
    // Setting here makes it work even when entry.js's `window.EstimateTemplate = ET`
    // line is skipped (e.g. namespace from raw ESM is empty).
    window.EstimateTemplate = __exports;
}
